import { useState } from 'react';
import { OptionRow } from '../components/Controls.tsx';
import type { PiProfile } from '../../shared/api.ts';

/**
 * SAVED PI PROVIDER SETUPS — the ⚙ row that makes "which provider builds the
 * best decks" a question someone can actually run.
 *
 * Pi exists so that changing provider is configuration rather than a release,
 * and that made comparing providers the obvious next thing to do. Doing it
 * meant retyping four fields — endpoint, wire format, model, auth mode — every
 * time you switched, and again to switch back, with the wire format being the
 * one that kills a build outright if you misremember it. So the setups are
 * saved and switching is a click.
 *
 * A PROFILE IS A SNAPSHOT, NOT A SOURCE OF TRUTH. Applying one copies its
 * values into the live fields, which is what every resolver on the build path
 * already reads; nothing downstream learns that profiles exist. That is
 * deliberate — it keeps a malformed profile no more dangerous than a
 * mistyped field, and it means there is no "active profile" pointer that can
 * disagree with what a build will actually do. Which profile is current is
 * DERIVED, by comparing the live fields to each saved one.
 *
 * It holds no API key. See `LLMSettings.piProfiles` for why, and for what that
 * costs: most provider variety is reachable through Pi's own OAuth logins,
 * which need no key at all.
 */

/** The live Pi fields a profile mirrors. Same shape as the saved profile minus
 *  its label, so `matches` can compare them field by field. */
export type PiProfileFields = {
  auth: 'subscription' | 'api';
  subscriptionProvider?: string | null;
  baseUrl?: string | null;
  api?: 'openai-completions' | 'openai-responses' | null;
  model?: string | null;
};

/** Compare a saved profile with the live fields.
 *
 *  Normalised on BOTH sides before comparing, because the two arrive by
 *  different routes: the live values come from text inputs (blank string) and
 *  the saved ones from JSON (absent or null). Untrimmed, `''` and `undefined`
 *  would read as different setups and the drawer would say nothing is applied
 *  the moment you saved something. The wire format defaults to completions on
 *  both sides for the same reason — it is what a blank field means. */
export function matchesProfile(p: PiProfile, live: PiProfileFields): boolean {
  const norm = (v: string | null | undefined) => (typeof v === 'string' ? v.trim() : '');
  const api = (v: string | null | undefined) => (v === 'openai-responses' ? 'openai-responses' : 'openai-completions');
  if (p.auth !== live.auth) return false;
  if (p.auth === 'subscription') {
    // The endpoint and wire format are the login's business on this branch, so
    // two subscription profiles differing only there are the same setup.
    return norm(p.subscriptionProvider) === norm(live.subscriptionProvider) && norm(p.model) === norm(live.model);
  }
  return norm(p.baseUrl) === norm(live.baseUrl) && api(p.api) === api(live.api) && norm(p.model) === norm(live.model);
}

/** One line describing where a profile points, for the list. */
function describe(p: PiProfile): string {
  const model = (p.model || '').trim() || 'no model';
  if (p.auth === 'subscription') return `${(p.subscriptionProvider || 'subscription').trim()} · ${model}`;
  let host = (p.baseUrl || '').trim() || 'no endpoint';
  try {
    host = new URL(host).host;
  } catch {
    /* not a URL: show what is stored, it is the fault anyway */
  }
  const wire = p.api === 'openai-responses' ? 'Responses' : 'chat completions';
  return `${host} · ${model} · ${wire} · metered`;
}

export function PiProfiles({
  profiles,
  onProfiles,
  current,
  onApply,
}: {
  profiles: PiProfile[];
  onProfiles: (next: PiProfile[]) => void;
  current: PiProfileFields;
  onApply: (p: PiProfile) => void;
}) {
  const [label, setLabel] = useState('');
  const activeIndex = profiles.findIndex((p) => matchesProfile(p, current));

  const save = () => {
    const name = label.trim().slice(0, 60);
    if (!name) return;
    const entry: PiProfile = { label: name, ...current };
    // Same name replaces, rather than accumulating near-duplicates: someone
    // re-saving "OpenCode Go" after changing its model means to update it.
    const i = profiles.findIndex((p) => p.label.toLowerCase() === name.toLowerCase());
    onProfiles(i >= 0 ? profiles.map((p, n) => (n === i ? entry : p)) : [...profiles, entry]);
    setLabel('');
  };

  return (
    <>
      <OptionRow
        label="Saved providers"
        sub
        stack
        hint={
          profiles.length === 0
            ? 'Nothing saved yet. Set the endpoint, model and wire format below, name the setup and save it — then switching providers is one click, and switching back does not mean retyping. Decks record which provider built them, so a comparison stays labelled.'
            : activeIndex >= 0
              ? `Pi is set up as “${profiles[activeIndex]!.label}”. Applying another fills in every field below; nothing is saved until you save the drawer.`
              : 'The fields below do not match any saved setup. Apply one to switch, or name the current one and save it.'
        }
      >
        {/* ONE ROW, NOT ONE PER PROFILE. The first version rendered a `.cred`
            per saved setup; `.opt-row__control` lays its children out in a row,
            so two profiles sat side by side with their Apply/Delete buttons
            wrapping onto a line below — detached from the setup they belonged
            to, which is worse than useless on a Delete button. A select is also
            simply the right control: this is an exclusive choice among saved
            setups, and the drawer already uses selects for exactly that. */}
        <div className="cred-stack">
          <div className="cred">
            <select
              value={activeIndex >= 0 ? String(activeIndex) : ''}
              onChange={(e) => {
                const p = profiles[Number(e.target.value)];
                if (p) onApply(p);
              }}
              aria-label="Saved Pi provider setups"
              disabled={profiles.length === 0}
            >
              {/* The unsaved state is a real option, not a blank: the fields can
                genuinely match nothing saved, and the select must be able to
                say so rather than pointing at a setup that is not applied. */}
              {activeIndex < 0 && <option value="">Current settings (not saved)</option>}
              {profiles.map((p, i) => (
                <option key={`${p.label}-${i}`} value={String(i)}>
                  {p.label} — {describe(p)}
                </option>
              ))}
              {profiles.length === 0 && <option value="">Nothing saved yet</option>}
            </select>
            <button
              type="button"
              className="btn btn--sm btn--ghost"
              disabled={activeIndex < 0}
              onClick={() => onProfiles(profiles.filter((_, n) => n !== activeIndex))}
              title={
                activeIndex < 0 ? 'Nothing to delete: these fields are not a saved setup' : 'Delete this saved setup'
              }
            >
              Delete
            </button>
          </div>
          <div className="cred">
            <input
              type="text"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  save();
                }
              }}
              placeholder="Name this setup, e.g. OpenCode Go · luna"
              aria-label="Name for this provider setup"
              maxLength={60}
            />
            <button type="button" className="btn btn--sm" onClick={save} disabled={!label.trim()}>
              Save current
            </button>
          </div>
        </div>
      </OptionRow>
    </>
  );
}
